/**
 * Sprint 73 — API publique tenant : clés + webhooks + livraisons.
 * Gated par `api_public` (Enterprise, Cabinet).
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Key, Webhook, Plus, Trash2, Copy, RefreshCw, Send, Ban, CheckCircle2,
  AlertCircle, Clock, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const fmtDt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
};

export default function ApiSettings() {
  const [tab, setTab] = useState("keys");
  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">API & Webhooks</h1>
        <p className="text-sm text-slate-500 mt-1">
          Clés API publiques et webhooks HMAC pour synchroniser Averdy Agency avec vos systèmes tiers.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="keys" data-testid="tab-api-keys">
            <Key className="w-4 h-4 mr-1" />Clés API
          </TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks">
            <Webhook className="w-4 h-4 mr-1" />Webhooks
          </TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">
            <ExternalLink className="w-4 h-4 mr-1" />Documentation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="mt-4">
          <ApiKeysPanel />
        </TabsContent>
        <TabsContent value="webhooks" className="mt-4">
          <WebhooksPanel />
        </TabsContent>
        <TabsContent value="docs" className="mt-4">
          <DocsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// CLÉS API
// ============================================================================
function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [revealed, setRevealed] = useState(null); // {key, name}

  const load = async () => {
    try {
      const r = await api.get("/api-keys");
      setKeys(r.data.keys || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await api.post("/api-keys", { name: newName, scopes: ["read"] });
      setRevealed({ key: r.data.key, name: r.data.name });
      setCreating(false); setNewName("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const revoke = async (id, name) => {
    if (!window.confirm(`Révoquer la clé « ${name} » ? Cette action est irréversible.`)) return;
    try {
      await api.post(`/api-keys/${id}/revoke`);
      toast.success("Clé révoquée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette clé ?")) return;
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success("Clé supprimée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const copy = (val, label = "Clé") => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copiée dans le presse-papier`);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="w-4 h-4 text-amber-600" />Clés API tenant
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Utilisez ces clés dans le header{" "}
              <code className="bg-slate-100 rounded px-1 text-xs">Authorization: Bearer clx_live_...</code>{" "}
              pour appeler{" "}
              <code className="bg-slate-100 rounded px-1 text-xs">/api/v1/*</code>.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="new-api-key-btn">
            <Plus className="w-4 h-4 mr-1" />Nouvelle clé
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {keys.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            Aucune clé API. Créez-en une pour commencer.
          </div>
        ) : (
          <div className="border rounded-lg divide-y">
            {keys.map((k) => (
              <div key={k.id} className="p-3 flex items-center justify-between"
                    data-testid={`api-key-${k.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{k.name}</p>
                    {!k.active && (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Révoquée</Badge>
                    )}
                    {k.active && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                    <span className="font-mono">{k.masked}</span>
                    <span>Créée : {fmtDt(k.created_at)}</span>
                    <span>Dernière utilisation : {fmtDt(k.last_used_at)}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {k.active && (
                    <Button variant="ghost" size="sm" onClick={() => revoke(k.id, k.name)}
                             data-testid={`revoke-${k.id}`}>
                      <Ban className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => remove(k.id)}
                           data-testid={`delete-${k.id}`}>
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog création */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle clé API</DialogTitle>
            <DialogDescription>
              La clé ne sera affichée qu'UNE SEULE FOIS. Stockez-la immédiatement dans un coffre.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom (usage interne)</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                     placeholder="Intégration Zapier · Sync CRM"
                     data-testid="key-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Annuler</Button>
            <Button onClick={create} disabled={!newName.trim()}
                     data-testid="create-key-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog reveal clé */}
      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Clé créée : {revealed?.name}
            </DialogTitle>
            <DialogDescription className="text-red-600 font-medium">
              ⚠ Copiez cette clé maintenant. Elle ne sera plus jamais affichée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="bg-slate-900 text-emerald-400 p-3 rounded font-mono text-xs break-all"
                 data-testid="revealed-key">
              {revealed?.key}
            </div>
            <Button onClick={() => copy(revealed?.key)} className="w-full"
                    data-testid="copy-key-btn">
              <Copy className="w-4 h-4 mr-2" />Copier la clé
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealed(null)}>J'ai copié la clé</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================================
// WEBHOOKS
// ============================================================================
function WebhooksPanel() {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ url: "", description: "", events: [] });
  const [revealed, setRevealed] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [selectedForDeliveries, setSelectedForDeliveries] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  const load = async () => {
    try {
      const [w, e] = await Promise.all([
        api.get("/webhooks"), api.get("/webhooks/events"),
      ]);
      setWebhooks(w.data.webhooks || []);
      setEvents(e.data.events || []);
    } catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const r = await api.post("/webhooks", form);
      setRevealed({ secret: r.data.secret, url: r.data.url });
      setCreating(false); setForm({ url: "", description: "", events: [] });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleEvent = (evt) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(evt)
        ? f.events.filter((x) => x !== evt) : [...f.events, evt],
    }));
  };

  const testDelivery = async (id) => {
    try {
      const r = await api.post(`/webhooks/${id}/test`);
      setTestResult(r.data.delivery);
    } catch (e) { toast.error(apiError(e)); }
  };

  const rotate = async (id) => {
    if (!window.confirm("Régénérer le secret ? L'ancien cessera d'être valide immédiatement.")) return;
    try {
      const r = await api.post(`/webhooks/${id}/rotate-secret`);
      setRevealed({ secret: r.data.secret, url: r.data.url });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce webhook ?")) return;
    try {
      await api.delete(`/webhooks/${id}`);
      toast.success("Webhook supprimé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const showDeliveries = async (wh) => {
    setSelectedForDeliveries(wh);
    try {
      const r = await api.get(`/webhooks/${wh.id}/deliveries`);
      setDeliveries(r.data.deliveries || []);
    } catch (e) { toast.error(apiError(e)); setDeliveries([]); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Webhook className="w-4 h-4 text-purple-600" />Webhooks HMAC
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Recevez les events Averdy Agency en temps réel sur votre URL. Chaque payload est signé HMAC-SHA256.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="new-webhook-btn">
            <Plus className="w-4 h-4 mr-1" />Nouveau webhook
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {webhooks.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            Aucun webhook. Créez-en un pour être notifié des events.
          </div>
        ) : (
          <div className="space-y-2">
            {webhooks.map((w) => (
              <div key={w.id} className="border rounded-lg p-3" data-testid={`webhook-${w.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium truncate">{w.url}</p>
                      {w.active
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Actif</Badge>
                        : <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Inactif</Badge>}
                      {w.failure_count > 0 && (
                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                          <AlertCircle className="w-3 h-3 mr-1" />{w.failure_count} échec(s)
                        </Badge>
                      )}
                    </div>
                    {w.description && (
                      <p className="text-sm text-slate-500 mt-1">{w.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {w.events.map((e) => (
                        <Badge key={e} variant="outline" className="text-xs">{e}</Badge>
                      ))}
                    </div>
                    <div className="text-xs text-slate-400 mt-2 flex gap-3">
                      <span>Créé : {fmtDt(w.created_at)}</span>
                      <span>Dernière livraison : {fmtDt(w.last_delivery_at)}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => testDelivery(w.id)}
                             data-testid={`test-${w.id}`} title="Envoyer un test">
                      <Send className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => showDeliveries(w)}
                             data-testid={`log-${w.id}`} title="Livraisons">
                      <Clock className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => rotate(w.id)}
                             data-testid={`rotate-${w.id}`} title="Régénérer secret">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(w.id)}
                             data-testid={`delete-${w.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog création webhook */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouveau webhook</DialogTitle>
            <DialogDescription>
              L'URL doit répondre en 2xx dans 10 secondes. En cas d'échec, 3 tentatives sont faites.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>URL destination</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                     placeholder="https://api.mon-service.com/clarix/webhook"
                     data-testid="webhook-url-input" />
            </div>
            <div>
              <Label>Description (optionnel)</Label>
              <Input value={form.description}
                     onChange={(e) => setForm({ ...form, description: e.target.value })}
                     placeholder="Synchro CRM · Notification Slack" />
            </div>
            <div>
              <Label>Events à souscrire ({form.events.length})</Label>
              <div className="border rounded p-2 max-h-48 overflow-y-auto grid grid-cols-2 gap-1 mt-1">
                {events.map((evt) => (
                  <label key={evt} className="flex items-center gap-2 text-sm py-1 px-1 hover:bg-slate-50 rounded cursor-pointer">
                    <input id={`event-${evt}`} name="webhook-events"
                           type="checkbox" checked={form.events.includes(evt)}
                           onChange={() => toggleEvent(evt)}
                           data-testid={`event-${evt}`} />
                    <span className="font-mono text-xs">{evt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Annuler</Button>
            <Button onClick={create}
                     disabled={!form.url || form.events.length === 0}
                     data-testid="create-webhook-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog reveal secret */}
      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />Secret webhook
            </DialogTitle>
            <DialogDescription className="text-red-600 font-medium">
              ⚠ Copiez ce secret. Vous l'utiliserez pour vérifier la signature HMAC.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">URL : <span className="font-mono">{revealed?.url}</span></p>
            <div className="bg-slate-900 text-emerald-400 p-3 rounded font-mono text-xs break-all"
                 data-testid="revealed-secret">{revealed?.secret}</div>
            <Button onClick={() => {navigator.clipboard.writeText(revealed?.secret); toast.success("Secret copié");}}
                    className="w-full">
              <Copy className="w-4 h-4 mr-2" />Copier le secret
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealed(null)}>J'ai copié</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog test result */}
      <Dialog open={!!testResult} onOpenChange={(o) => !o && setTestResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Résultat du test</DialogTitle>
            <DialogDescription>
              Détails de la dernière tentative d'appel du webhook (code HTTP, tentatives, corps).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>Statut : <Badge className={testResult?.status === "success"
              ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
              {testResult?.status}</Badge></p>
            <p>Code HTTP : <span className="font-mono">{testResult?.status_code ?? "—"}</span></p>
            <p>Tentatives : {testResult?.attempts}</p>
            {testResult?.response_body && (
              <div>
                <Label className="text-xs">Réponse</Label>
                <pre className="bg-slate-100 p-2 rounded text-xs max-h-48 overflow-auto">
                  {testResult.response_body}
                </pre>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setTestResult(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog deliveries */}
      <Dialog open={!!selectedForDeliveries}
              onOpenChange={(o) => !o && setSelectedForDeliveries(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Historique livraisons — {selectedForDeliveries?.url}</DialogTitle>
            <DialogDescription>
              20 dernières tentatives d'envoi vers ce webhook, avec code HTTP, statut et durée.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {deliveries.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">Aucune livraison encore.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-slate-500 border-b">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Event</th>
                    <th className="text-left p-2">Statut</th>
                    <th className="text-left p-2">HTTP</th>
                    <th className="text-left p-2">Tentatives</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => (
                    <tr key={d.id} className="border-b hover:bg-slate-50">
                      <td className="p-2">{fmtDt(d.created_at)}</td>
                      <td className="p-2 font-mono">{d.event}</td>
                      <td className="p-2">
                        <Badge className={d.status === "success"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700"}>{d.status}</Badge>
                      </td>
                      <td className="p-2">{d.status_code ?? "—"}</td>
                      <td className="p-2">{d.attempts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================================
// DOCS
// ============================================================================
function DocsPanel() {
  return (
    <Card>
      <CardContent className="p-6 space-y-6 text-sm">
        <div>
          <h3 className="font-semibold text-base mb-2">🔑 Authentification</h3>
          <p className="text-slate-600 mb-2">
            Ajoutez votre clé API dans le header <code className="bg-slate-100 rounded px-1">Authorization</code> :
          </p>
          <pre className="bg-slate-900 text-emerald-400 p-3 rounded text-xs overflow-auto">
{`curl https://votre-clarix.com/api/v1/invoices \\
  -H "Authorization: Bearer clx_live_xxxxxxxxxxxx"`}</pre>
        </div>

        <div>
          <h3 className="font-semibold text-base mb-2">📥 Endpoints v1 (read-only)</h3>
          <ul className="space-y-1 font-mono text-xs text-slate-700">
            <li>GET /api/v1/me — infos tenant + clé courante</li>
            <li>GET /api/v1/invoices?limit=50&amp;offset=0</li>
            <li>GET /api/v1/purchases?limit=50&amp;offset=0</li>
            <li>GET /api/v1/contracts?limit=50&amp;offset=0</li>
            <li>GET /api/v1/clients?limit=50&amp;offset=0</li>
            <li>GET /api/v1/events — catalogue events webhooks</li>
          </ul>
        </div>

        <div>
          <h3 className="font-semibold text-base mb-2">🔒 Vérifier une signature webhook (Python)</h3>
          <pre className="bg-slate-900 text-emerald-400 p-3 rounded text-xs overflow-auto">
{`import hmac, hashlib

def verify(secret: str, header: str, body: str) -> bool:
    parts = dict(p.split("=", 1) for p in header.split(","))
    ts, v1 = int(parts["t"]), parts["v1"]
    expected = hmac.new(secret.encode(),
                        f"{ts}.{body}".encode(),
                        hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, v1)`}</pre>
        </div>

        <div>
          <h3 className="font-semibold text-base mb-2">⚡ Rate limits</h3>
          <ul className="list-disc pl-6 text-slate-600 space-y-1">
            <li>100 requêtes / minute / clé API (fenêtre glissante)</li>
            <li>3 tentatives de livraison webhook (backoff 1s → 5s → 25s)</li>
            <li>Timeout webhook : 10 secondes</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
